import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { dispatch, store, RootState } from 'redux/store';
import {
  saveCurrent,
  clearCurrent,
  setPrefetchingStatus,
  setUseMapOrganizationBoundingBox,
  OrganizationEntity,
  PrefetchingStatus,
} from './redux';
import {
  getGetOrganizationsQueryKey,
  Organization as OrganizationDto,
  useGetOrganizations,
  getOrganizations,
  getOrganization,
  getGetOrganizationQueryKey,
} from 'api-client';
import { OrganizationMapper } from './mapper';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { QueryClient } from '@tanstack/react-query';
import { queryClient as moduleQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import { AxiosError } from 'axios';
import loggerService, { LoggerLevel } from '../log';
import AssetRepository from 'assetClass/infrastructure/assetClass/asset/repository';
import MapRepository from 'common/infrastructure/map/repository';

const OrganizationRepository = {
  useOrganizations() {
    const currentTenantPid = TenantRepository.getCurrentTenantPid();

    const useQueryResponse = useGetOrganizations(
      currentTenantPid,
      {
        tenantId: currentTenantPid,
      },
      {
        query: {
          enabled: !!currentTenantPid,
        },
      }
    );

    // Log errors automatically when they occur
    useEffect(() => {
      if (useQueryResponse.failureReason) {
        loggerService.log(
          LoggerLevel.ERROR,
          'Failed to load organizations',
          useQueryResponse.failureReason
        );
      }
    }, [useQueryResponse.failureReason]);

    const data: OrganizationDto[] | undefined = useQueryResponse?.data;
    const organizationEntities = data?.map(OrganizationMapper.fromDTO) || [];

    // Set tenantHasOrganizations on first successful load
    useEffect(() => {
      if (data) {
        TenantRepository.setTenantHasOrganizations(data.length > 0);
      }
    }, [data]);

    if (!data || data?.length === 0) {
      return {
        ...useQueryResponse,
        organizationEntities: [],
        isLoading: useQueryResponse.isLoading,
      };
    }

    return {
      ...useQueryResponse,
      organizationEntities,
      isLoading: useQueryResponse.isLoading,
    };
  },

  useCurrentOrganization() {
    const currentOrganization = useSelector(
      (state: RootState) => state.organizations.currentOrganization
    );
    const { organizationEntities, ...useQueryResponse } = this.useOrganizations();

    useEffect(() => {
      if (organizationEntities && organizationEntities.length > 0) {
        // If no currentOrganization or current organization is not in the list, default to first
        if (
          !currentOrganization ||
          !organizationEntities.find((org) => org.id === currentOrganization.id)
        ) {
          this.saveCurrent(organizationEntities[0]);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationEntities, currentOrganization?.id]);

    // Fetch organization details if current organization lacks bounding box
    useEffect(() => {
      const fetchOrganizationDetails = async () => {
        if (!currentOrganization?.id || currentOrganization.boundingBox) {
          return; // No organization or already has bounding box
        }

        try {
          const orgDetails = await this.getOrganizationDetails(currentOrganization.id);
          // Update the current organization with the detailed data (including bounding box)
          this.saveCurrent(orgDetails);
        } catch (error) {
          loggerService.log(
            LoggerLevel.ERROR,
            'Failed to fetch organization details for bounding box',
            error
          );
        }
      };

      fetchOrganizationDetails();
    }, [currentOrganization?.id, currentOrganization?.boundingBox]);

    // Asset prefetching logic moved to saveCurrent method to prevent redundant calls on component mount

    if (!organizationEntities || organizationEntities.length === 0) {
      return { organization: null, ...useQueryResponse };
    }

    if (useQueryResponse.failureReason) {
      const axiosResponse = useQueryResponse?.failureReason as AxiosError;
      const status = axiosResponse.response?.status;
      if (status === 404) {
        loggerService.log(LoggerLevel.ERROR, 'Organizations not found for tenant');
      }
    }

    // Return the current organization from Redux state if it exists, otherwise default to first
    let organization = currentOrganization;
    if (!organization || !organizationEntities.find((org) => org.id === organization?.id)) {
      organization = organizationEntities[0];
    }

    return {
      ...useQueryResponse,
      organization,
    };
  },

  getCurrentOrganization(): OrganizationEntity | null {
    const { organizations: organizationStore } = store.getState();
    return organizationStore.currentOrganization;
  },

  getPrefetchingStatus(): PrefetchingStatus {
    const { organizations: organizationStore } = store.getState();
    return organizationStore.prefetchingStatus;
  },

  usePrefetchingStatus() {
    return useSelector((state: RootState) => state.organizations.prefetchingStatus);
  },

  saveCurrent(organization: OrganizationEntity) {
    dispatch(saveCurrent(organization));

    // Prefetch assets when organization changes
    this.prefetchAssetsForOrganization(organization);
  },

  async prefetchAssetsForOrganization(organization: OrganizationEntity) {
    const tenantHasOrganizations = TenantRepository.getTenantHasOrganizations();

    // Don't prefetch if tenant has no organizations
    if (tenantHasOrganizations === false || !organization.id || !organization.boundingBox) {
      return;
    }

    const currentTenantPid = TenantRepository.getCurrentTenantPid();
    if (!currentTenantPid) {
      return;
    }

    // Get current asset class ID
    const { assetClasses }: { assetClasses: { currentAssetClassId: string | null } } =
      store.getState();
    const { currentAssetClassId } = assetClasses;

    if (!currentAssetClassId) {
      return;
    }

    // Set prefetching status to loading
    dispatch(setPrefetchingStatus('loading'));

    try {
      MapRepository.setInitialBoundingBox(organization.boundingBox);
      await AssetRepository.prefetchAssets(currentAssetClassId, {
        tp2OrganizationId: organization.tp2OrganizationId,
        boundingBox: organization.boundingBox,
      });

      // Set prefetching status to completed on success
      dispatch(setPrefetchingStatus('completed'));
    } catch (error) {
      // Set prefetching status to error on failure
      dispatch(setPrefetchingStatus('error'));
      loggerService.log(LoggerLevel.ERROR, 'Failed to prefetch assets for organization', error);
    }
  },

  clearCurrent() {
    dispatch(clearCurrent());
  },

  async refetchOrganizations(queryClient: QueryClient, tenantId: string) {
    await queryClient.invalidateQueries({
      queryKey: getGetOrganizationsQueryKey(tenantId, { tenantId }),
    });
  },

  async prefetchOrganizations(tenantId: string) {
    const queryKey = getGetOrganizationsQueryKey(tenantId, { tenantId });
    await moduleQueryClient.prefetchQuery({
      queryKey,
      queryFn: () => getOrganizations(tenantId, { tenantId }),
    });
  },

  async getOrganizationDetails(organizationId: string): Promise<OrganizationEntity> {
    const tenantId = TenantRepository.getCurrentTenantPid();
    const currentOrganization = this.getCurrentOrganization();
    const tp2OrganizationId = currentOrganization?.tp2OrganizationId;

    if (!tenantId) {
      throw new Error('No tenant available for organization details fetch');
    }

    const queryKey = getGetOrganizationQueryKey(tenantId, { tenantId, organizationId });
    const organizationDto = await moduleQueryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getOrganization(tenantId, {
          tenantId,
          organizationId,
          tp2OrganizationId: tp2OrganizationId ?? undefined,
          getBoundingBox: true,
        }),
    });

    return OrganizationMapper.fromDTO(organizationDto);
  },

  setUseMapOrganizationBoundingBox(useMapOrganizationBoundingBox: boolean) {
    dispatch(setUseMapOrganizationBoundingBox(useMapOrganizationBoundingBox));
  },

  getUseMapOrganizationBoundingBox() {
    return store.getState().organizations.useMapOrganizationBoundingBox;
  },
};

export default OrganizationRepository;
